import { useState, useMemo, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, X, Package, AlertTriangle } from "lucide-react";
import { StockTakeFormData, SelectedStockItem } from "@/pages/stock-take-create";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SetupStepProps {
  formData: StockTakeFormData;
  updateFormData: (updates: Partial<StockTakeFormData>) => void;
}

// Mock stock items data
const mockStockItems = [
  { code: "STK001", description: "Laptop Dell XPS 15", category: "Electronics", location: "Warehouse A", physicalQuantity: 45, unitPrice: 1299.99 },
  { code: "STK002", description: "Office Chair Ergonomic", category: "Furniture", location: "Warehouse B", physicalQuantity: 120, unitPrice: 249.50 },
  { code: "STK003", description: "A4 Paper Ream (500 sheets)", category: "Office Supplies", location: "Warehouse A", physicalQuantity: 450, unitPrice: 5.99 },
  { code: "STK004", description: "Steel Rods 10mm x 6m", category: "Raw Materials", location: "Distribution Center", physicalQuantity: 200, unitPrice: 45.75 },
  { code: "STK005", description: "Wireless Mouse Logitech", category: "Electronics", location: "Retail Store", physicalQuantity: 85, unitPrice: 29.99 },
  { code: "STK006", description: "Standing Desk Adjustable", category: "Furniture", location: "Warehouse B", physicalQuantity: 35, unitPrice: 599.00 },
  { code: "STK007", description: "Ballpoint Pens Blue (Box of 50)", category: "Office Supplies", location: "Warehouse A", physicalQuantity: 300, unitPrice: 12.50 },
  { code: "STK008", description: "Aluminum Sheets 2mm", category: "Raw Materials", location: "Warehouse C", physicalQuantity: 150, unitPrice: 89.99 },
  { code: "STK009", description: "Monitor 27\" 4K Dell", category: "Electronics", location: "Warehouse A", physicalQuantity: 60, unitPrice: 449.99 },
  { code: "STK010", description: "Filing Cabinet 4-Drawer", category: "Furniture", location: "Warehouse B", physicalQuantity: 40, unitPrice: 189.99 },
  { code: "STK011", description: "Stapler Heavy Duty", category: "Office Supplies", location: "Retail Store", physicalQuantity: 95, unitPrice: 18.99 },
  { code: "STK012", description: "Copper Wire 2.5mm", category: "Raw Materials", location: "Distribution Center", physicalQuantity: 500, unitPrice: 3.25 },
  { code: "STK013", description: "Keyboard Mechanical RGB", category: "Electronics", location: "Warehouse A", physicalQuantity: 70, unitPrice: 129.99 },
  { code: "STK014", description: "Conference Table 8-Seater", category: "Furniture", location: "Warehouse B", physicalQuantity: 12, unitPrice: 899.00 },
  { code: "STK015", description: "Sticky Notes 3x3 (Pack of 12)", category: "Office Supplies", location: "Warehouse A", physicalQuantity: 280, unitPrice: 8.99 },
  { code: "STK016", description: "PVC Pipes 50mm x 3m", category: "Raw Materials", location: "Warehouse C", physicalQuantity: 180, unitPrice: 15.50 },
  { code: "STK017", description: "Webcam HD 1080p", category: "Electronics", location: "Retail Store", physicalQuantity: 55, unitPrice: 79.99 },
  { code: "STK018", description: "Bookshelf 5-Tier", category: "Furniture", location: "Warehouse B", physicalQuantity: 65, unitPrice: 149.99 },
  { code: "STK019", description: "Printer Paper A3 (250 sheets)", category: "Office Supplies", location: "Warehouse A", physicalQuantity: 220, unitPrice: 9.99 },
  { code: "STK020", description: "Timber Planks Oak 2x4", category: "Raw Materials", location: "Distribution Center", physicalQuantity: 300, unitPrice: 12.75 },
  { code: "STK021", description: "USB-C Hub 7-Port", category: "Electronics", location: "Warehouse A", physicalQuantity: 90, unitPrice: 45.99 },
  { code: "STK022", description: "Desk Lamp LED Adjustable", category: "Furniture", location: "Retail Store", physicalQuantity: 110, unitPrice: 34.99 },
  { code: "STK023", description: "Whiteboard Markers (Set of 8)", category: "Office Supplies", location: "Warehouse A", physicalQuantity: 175, unitPrice: 14.99 },
  { code: "STK024", description: "Stainless Steel Bolts M8", category: "Raw Materials", location: "Warehouse C", physicalQuantity: 1000, unitPrice: 0.45 },
  { code: "STK025", description: "External SSD 1TB Samsung", category: "Electronics", location: "Warehouse A", physicalQuantity: 40, unitPrice: 149.99 },
];

const stockTakeTypes = [
  { 
    value: "Full Stock Take", 
    label: "Full Stock Take", 
    disabled: false,
    description: "Count all items in the warehouse"
  },
  { 
    value: "Targeted Count", 
    label: "Targeted Count", 
    disabled: false,
    description: "Count selected items or locations"
  },
  { 
    value: "Cycle Count", 
    label: "Cycle Count (Future Implementation)", 
    disabled: true,
    description: "Scheduled recurring counts"
  },
];

export function StockTakeSetupStep({ formData, updateFormData }: SetupStepProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");

  // Determine behavior based on stock take type
  const isFullStockTake = formData.type === "Full Stock Take";
  const isTargetedCount = formData.type === "Targeted Count";

  // Get unique categories and locations
  const categories = useMemo(
    () => Array.from(new Set(mockStockItems.map((item) => item.category))),
    []
  );
  const locations = useMemo(
    () => Array.from(new Set(mockStockItems.map((item) => item.location))),
    []
  );

  // Auto-select all items for Full Stock Take
  useEffect(() => {
    if (isFullStockTake && formData.selectedItems.length !== mockStockItems.length) {
      const allItems: SelectedStockItem[] = mockStockItems.map((item) => ({
        code: item.code,
        description: item.description,
        category: item.category,
        location: item.location,
        expectedQuantity: item.physicalQuantity,
        unitPrice: item.unitPrice,
      }));
      updateFormData({ selectedItems: allItems });
    }
  }, [isFullStockTake]);

  // Auto-select items by location for Targeted Count when location is selected
  useEffect(() => {
    if (isTargetedCount && locationFilter !== "all") {
      const locationItems = mockStockItems
        .filter((item) => item.location === locationFilter)
        .map((item) => ({
          code: item.code,
          description: item.description,
          category: item.category,
          location: item.location,
          expectedQuantity: item.physicalQuantity,
          unitPrice: item.unitPrice,
        }));
      updateFormData({ selectedItems: locationItems });
    }
  }, [isTargetedCount, locationFilter]);

  // Filter stock items
  const filteredItems = useMemo(() => {
    let filtered = mockStockItems;

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter((item) => item.category === categoryFilter);
    }

    // Apply location filter
    if (locationFilter !== "all") {
      filtered = filtered.filter((item) => item.location === locationFilter);
    }

    return filtered;
  }, [searchQuery, categoryFilter, locationFilter]);

  // Check if item is selected
  const isItemSelected = (code: string) => {
    return formData.selectedItems.some((item) => item.code === code);
  };

  // Toggle item selection
  const toggleItemSelection = (item: typeof mockStockItems[0]) => {
    const isSelected = isItemSelected(item.code);

    if (isSelected) {
      updateFormData({
        selectedItems: formData.selectedItems.filter((i) => i.code !== item.code),
      });
    } else {
      const newItem: SelectedStockItem = {
        code: item.code,
        description: item.description,
        category: item.category,
        location: item.location,
        expectedQuantity: item.physicalQuantity,
        unitPrice: item.unitPrice,
      };
      updateFormData({
        selectedItems: [...formData.selectedItems, newItem],
      });
    }
  };

  // Select all filtered items
  const selectAllFiltered = () => {
    const newItems = filteredItems
      .filter((item) => !isItemSelected(item.code))
      .map((item) => ({
        code: item.code,
        description: item.description,
        category: item.category,
        location: item.location,
        expectedQuantity: item.physicalQuantity,
        unitPrice: item.unitPrice,
      }));

    updateFormData({
      selectedItems: [...formData.selectedItems, ...newItems],
    });
  };

  // Deselect all
  const deselectAll = () => {
    updateFormData({ selectedItems: [] });
  };

  // Format date for input
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div>
        <h2 className="text-2xl font-semibold mb-1">Stock Take Setup</h2>
        <p className="text-muted-foreground">
          Configure the basic details and select items for your stock take
        </p>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Date */}
        <div className="space-y-2">
          <Label htmlFor="date">
            Date <span className="text-destructive">*</span>
          </Label>
          <Input
            id="date"
            type="date"
            value={formatDateForInput(formData.date)}
            onChange={(e) => updateFormData({ date: new Date(e.target.value) })}
            required
          />
        </div>

        {/* Type */}
        <div className="space-y-2">
          <Label htmlFor="type">
            Type <span className="text-destructive">*</span>
          </Label>
          <Select value={formData.type} onValueChange={(value) => updateFormData({ type: value })}>
            <SelectTrigger id="type">
              <SelectValue placeholder="Select stock take type" />
            </SelectTrigger>
            <SelectContent>
              {stockTakeTypes.map((type) => (
                <SelectItem 
                  key={type.value} 
                  value={type.value}
                  disabled={type.disabled}
                  className={type.disabled ? "text-muted-foreground italic" : ""}
                >
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Description */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="description">Description (Optional)</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => updateFormData({ description: e.target.value })}
            placeholder="Add any notes or instructions for this stock take"
            rows={3}
          />
        </div>
      </div>

      {/* Stock Take Type Info Banner */}
      {formData.type && (
        <div className={`rounded-lg border p-4 ${
          isFullStockTake 
            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" 
            : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
        }`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className={`h-5 w-5 mt-0.5 ${
              isFullStockTake 
                ? "text-blue-600 dark:text-blue-400" 
                : "text-green-600 dark:text-green-400"
            }`} />
            <div className="flex-1">
              <h4 className={`font-semibold mb-1 ${
                isFullStockTake 
                  ? "text-blue-900 dark:text-blue-100" 
                  : "text-green-900 dark:text-green-100"
              }`}>
                {formData.type}
              </h4>
              <p className={`text-sm ${
                isFullStockTake 
                  ? "text-blue-800 dark:text-blue-200" 
                  : "text-green-800 dark:text-green-200"
              }`}>
                {isFullStockTake && (
                  <>All {mockStockItems.length} items have been automatically selected. You cannot deselect items in a Full Stock Take.</>
                )}
                {isTargetedCount && (
                  <>Select specific items or locations to count. Use filters and search to find items, then select the ones you want to include.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stock Item Selection */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Select Stock Items</h3>
            <p className="text-sm text-muted-foreground">
              Choose the items to include in this stock take
            </p>
          </div>
          <Badge variant="secondary" className="text-base px-3 py-1">
            {formData.selectedItems.length} selected
          </Badge>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by code or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
              disabled={isFullStockTake}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Select 
            value={categoryFilter} 
            onValueChange={setCategoryFilter}
            disabled={isFullStockTake}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Select 
              value={locationFilter} 
              onValueChange={setLocationFilter}
              disabled={isFullStockTake}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location} value={location}>
                    {location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(searchQuery || categoryFilter !== "all" || locationFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery("");
                setCategoryFilter("all");
                setLocationFilter("all");
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Bulk Actions */}
        {!isFullStockTake && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={selectAllFiltered}>
              Select All ({filteredItems.length})
            </Button>
            {formData.selectedItems.length > 0 && (
              <Button variant="outline" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>
            )}
          </div>
        )}

        {/* Items Table */}
        <div className="rounded-lg border">
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-12">
                    <span className="sr-only">Select</span>
                  </TableHead>
                  <TableHead className="font-semibold">Code</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="font-semibold">Location</TableHead>
                  <TableHead className="font-semibold text-right">
                    Expected Qty
                  </TableHead>
                  <TableHead className="font-semibold text-right">
                    Unit Price
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <TableRow
                      key={item.code}
                      className={`${!isFullStockTake ? "cursor-pointer" : ""} ${
                        index % 2 === 0 ? "bg-muted/30" : ""
                      } ${isItemSelected(item.code) ? "bg-primary/5" : ""}`}
                      onClick={() => !isFullStockTake && toggleItemSelection(item)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isItemSelected(item.code)}
                          onCheckedChange={() => toggleItemSelection(item)}
                          disabled={isFullStockTake}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-medium">
                        {item.code}
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.category}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.location}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.physicalQuantity}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        £{item.unitPrice.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <Package className="h-12 w-12 text-muted-foreground" />
                        <p className="text-muted-foreground">No items found</p>
                        <p className="text-sm text-muted-foreground">
                          Try adjusting your search or filters
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        {/* Validation Message */}
        {formData.selectedItems.length === 0 && !isFullStockTake && (
          <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Please select at least one stock item to proceed
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
